import { useNavigate } from "react-router-dom";
import { Armchair, Pencil, Trash2, FileText, Undo2, Ban, ShieldCheck, Receipt } from "lucide-react";
import { downloadUrl } from "../../lib/api";
import { fmtTL, fmtDate, fmtCreator } from "../../lib/format";
import { todayStr } from "./assetShared";

const HEADERS = ["Demirbaş Adı", "Mahiyeti", "Cinsi", "Seri No", "Demirbaş Muh.Hs.", "Amortisman Muh.Hs.", "Amortisman Kodu", "Maliyet Değeri", "Alış Tarihi", "Faydalı Ömür", "Kalan Yıl", "Son Amortisman Yılı", "Zimmetli Proje", "Zimmetli Kişi", "Sonraki Bakım", "Garanti Bitiş", "Bakım Maliyeti", "Oluşturan"];
const TD = "px-3 py-2 text-xs";
const in30Days = () => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
// garanti: bitti gri, 30 gün içinde kırmızı, aksi yeşil
const warrantyCls = (d) => (d < todayStr() ? "bg-slate-200 text-slate-600" : d <= in30Days() ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800");

function AssigneeCell({ a }) {
  if (a.assignee_name) {
    return (
      <span className="inline-flex items-start gap-1.5">
        <span>{a.assignee_name}<span className="block text-[10px] text-slate-500">{fmtDate(a.assigned_at)}{(a.assignment_history || []).length > 1 ? ` · ${a.assignment_history.length} kayıt` : ""}</span></span>
        <a href={downloadUrl(`/fixed-assets/${a.id}/tutanak`)} target="_blank" rel="noreferrer" title="Zimmet tutanağı (PDF)" data-testid={`asset-tutanak-${a.id}`} className="text-rose-700 hover:text-rose-900 mt-0.5"><FileText className="w-3.5 h-3.5" /></a>
      </span>
    );
  }
  if ((a.assignment_history || []).some((h) => h.released_at)) {
    return <a href={downloadUrl(`/fixed-assets/${a.id}/teslim-tutanak`)} target="_blank" rel="noreferrer" title="Son iade/teslim tutanağı (PDF)" data-testid={`asset-teslim-${a.id}`} className="inline-flex items-center gap-1 text-sky-700 hover:underline"><Undo2 className="w-3 h-3" /> İade edildi</a>;
  }
  return "—";
}

function WarrantyCell({ a }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {a.warranty_end ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${warrantyCls(a.warranty_end)}`}>{fmtDate(a.warranty_end)}</span> : "—"}
      {a.warranty_doc_id && <a href={downloadUrl(`/asset-documents/${a.warranty_doc_id}/file`)} target="_blank" rel="noreferrer" title="Garanti belgesini aç" data-testid={`asset-warranty-doc-${a.id}`} className="text-emerald-700 hover:text-emerald-900"><ShieldCheck className="w-3.5 h-3.5" /></a>}
      {a.invoice_doc_id && <a href={downloadUrl(`/asset-documents/${a.invoice_doc_id}/file`)} target="_blank" rel="noreferrer" title="Faturayı aç" data-testid={`asset-invoice-doc-${a.id}`} className="text-sky-700 hover:text-sky-900"><Receipt className="w-3.5 h-3.5" /></a>}
    </span>
  );
}

function MaintCostCell({ a }) {
  if (!a.maintenance_count) return "—";
  return (
    <span title={`${a.maintenance_count} kayıt · ${a.fault_count} arıza`}>
      {fmtTL(a.maintenance_cost_total)}
      {a.maintenance_cost_ratio != null && <span className={`block text-[10px] ${a.maintenance_cost_ratio >= 50 ? "text-red-700 font-bold" : "text-slate-500"}`}>%{a.maintenance_cost_ratio} / maliyet</span>}
    </span>
  );
}

function AssetRow({ a, selected, toggleQr, userLabels, isAdmin, startEdit, onDispose, undoDispose, remove }) {
  const navigate = useNavigate();
  return (
    <tr className={`border-t border-slate-100 hover:bg-slate-50/60 ${selected ? "bg-amber-50/60" : ""} ${a.disposal ? "opacity-60" : ""}`} data-testid={`asset-row-${a.id}`}>
      <td className="px-3 py-2"><input type="checkbox" checked={selected} onChange={() => toggleQr(a.id)} data-testid={`qr-check-${a.id}`} /></td>
      <td className={`${TD} font-semibold text-slate-900 whitespace-nowrap`}><a href={`/demirbas/${a.id}`} onClick={(e) => { e.preventDefault(); navigate(`/demirbas/${a.id}`); }} className="hover:underline" data-testid={`asset-open-${a.id}`}>{a.name}</a>{a.disposal && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-800 text-white text-[9px] font-bold" data-testid={`asset-disposed-${a.id}`}>{a.disposal.type === "Satış" ? "SATILDI" : "HURDA"} {fmtDate(a.disposal.date)}</span>}</td>
      <td className={TD}>{a.nature || "—"}</td>
      <td className={TD}>{a.kind || "—"}</td>
      <td className={`${TD} font-mono`}>{a.serial_no || "—"}</td>
      <td className={`${TD} font-mono`}>{a.asset_account_code || "—"}</td>
      <td className={`${TD} font-mono`}>{a.depreciation_account_code || "—"}</td>
      <td className={`${TD} font-mono`}>{a.depreciation_code || "—"}</td>
      <td className={`numeric-cell ${TD} font-semibold whitespace-nowrap`}>{fmtTL(a.cost_value)}</td>
      <td className={`${TD} whitespace-nowrap`}>{fmtDate(a.purchase_date)}</td>
      <td className={`${TD} text-center`}>{a.useful_life_years ?? "—"}</td>
      <td className={`${TD} text-center font-semibold`} data-testid={`asset-remaining-${a.id}`}>{a.remaining_years ?? "—"}</td>
      <td className={`${TD} text-center`}>{a.last_depreciation_year ?? "—"}</td>
      <td className={`${TD} whitespace-nowrap`} data-testid={`asset-project-${a.id}`}>{a.project_name || "—"}</td>
      <td className={`${TD} whitespace-nowrap`} data-testid={`asset-assignee-${a.id}`}><AssigneeCell a={a} /></td>
      <td className={`${TD} whitespace-nowrap`} data-testid={`asset-next-maint-${a.id}`}>{a.next_maintenance_date ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${a.next_maintenance_date < todayStr() ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{fmtDate(a.next_maintenance_date)}</span> : "—"}</td>
      <td className={`${TD} whitespace-nowrap`} data-testid={`asset-warranty-${a.id}`}><WarrantyCell a={a} /></td>
      <td className={`numeric-cell ${TD} whitespace-nowrap`} data-testid={`asset-maint-cost-${a.id}`}><MaintCostCell a={a} /></td>
      <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{fmtCreator(a.created_by, a.created_at, userLabels)}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={() => startEdit(a)} className="text-slate-600 hover:text-slate-900 mr-2" title="Düzenle" data-testid={`asset-edit-${a.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        {!a.disposal && !a.assignee_personnel_id && <button onClick={() => onDispose(a)} className="text-slate-700 hover:text-black mr-2" title="Hurda / Satış" data-testid={`asset-dispose-${a.id}`}><Ban className="w-3.5 h-3.5" /></button>}
        {a.disposal && isAdmin && <button onClick={() => undoDispose(a)} className="text-sky-700 hover:text-sky-900 mr-2" title="Hurda/satış kaydını geri al" data-testid={`asset-undispose-${a.id}`}><Undo2 className="w-3.5 h-3.5" /></button>}
        {isAdmin && <button onClick={() => remove(a)} className="text-red-700 hover:text-red-900" title="Sil" data-testid={`asset-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
      </td>
    </tr>
  );
}

// mahiyet (üst hesap) bazında gruplu demirbaş tablosu; QR etiket seçimi ilk kolonda
export default function AssetTable({ items, grouped, qrSel, setQrSel, ...rowProps }) {
  const toggleQr = (id) => setQrSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="assets-table">
      {items.length === 0 ? (
        <div className="p-10 text-center">
          <Armchair className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <div className="text-sm font-semibold text-slate-700">Kayıtlı demirbaş yok</div>
          <div className="text-xs text-slate-500 mt-1">"Yeni Demirbaş Ekle" ile ilk kaydı oluşturun.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full tbl">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" title="QR etiket için tümünü seç" data-testid="qr-select-all" checked={items.length > 0 && qrSel.length === items.length} onChange={(e) => setQrSel(e.target.checked ? items.map((a) => a.id) : [])} /></th>
                {HEADERS.map((h) => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold whitespace-nowrap">{h}</th>)}
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-600 font-semibold">İşlem</th>
              </tr>
            </thead>
            {grouped.map(([nature, list]) => (
              <tbody key={nature}>
                <tr className="border-t-2 border-slate-300 bg-amber-50/70" data-testid={`asset-group-${nature.split(" - ")[0]}`}>
                  <td colSpan={20} className="px-3 py-2 text-xs font-bold text-amber-900 uppercase tracking-wide">
                    {nature} <span className="ml-2 font-semibold text-amber-700 normal-case tracking-normal">— {list.length} kayıt · Toplam {fmtTL(list.reduce((s, a) => s + (a.cost_value || 0), 0))}</span>
                  </td>
                </tr>
                {list.map((a) => <AssetRow key={a.id} a={a} selected={qrSel.includes(a.id)} toggleQr={toggleQr} {...rowProps} />)}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
}
